<template>
  <CCard class="weight-history-diff">
    <CCardBody>
      <div class="weight-history-diff__header">
        <div>
          <div class="weight-history-diff__eyebrow">Promjena</div>
          <h3 class="weight-history-diff__title">{{ selectedResolution.diffChartSubtitle }}</h3>
        </div>
        <div class="weight-history-diff__pill" :class="diffPillClass">
          {{ latestAverage }}
        </div>
      </div>
      <div class="weight-history-diff__canvas">
        <CChartBar class="weight-history-diff__canvas-element" :datasets="chartData" :options="chartOptions" :labels="chartLabels" />
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import { CChartBar } from "@coreui/vue-chartjs";
import moment from "moment";

const currentYear = new Date().getFullYear();
const getChartXAxisDateFormat = (date) => moment(date).format("DD.MM" + (moment(date).format("YYYY") == currentYear ? "" : ".YY"));
const parseDiff = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export default {
  components: { CChartBar },

  props: {
    averages: {
      type: Array,
      default: () => [],
    },
    selectedResolution: {
      type: Object,
      required: true,
    },
  },

  computed: {
    parsedDiffs() {
      return this.averages.map((entry) => parseDiff(entry.diffToLastAverage));
    },
    latestDiff() {
      for (let i = this.parsedDiffs.length - 1; i >= 0; i -= 1) {
        if (this.parsedDiffs[i] !== null) {
          return this.parsedDiffs[i];
        }
      }

      return null;
    },
    latestAverage() {
      if (this.latestDiff === null) {
        return "-";
      }

      return `${this.latestDiff > 0 ? "+" : ""}${this.latestDiff.toFixed(1)} kg`;
    },
    diffPillClass() {
      if (this.latestDiff === null) {
        return "weight-history-diff__pill--neutral";
      }

      return this.latestDiff <= 0 ? "weight-history-diff__pill--good" : "weight-history-diff__pill--bad";
    },
    chartMinMax() {
      const diffs = this.parsedDiffs.filter((value) => value !== null).map((value) => Math.abs(value));
      if (!diffs.length) {
        return 1;
      }

      return Math.max(1, Math.ceil(Math.max(...diffs)));
    },
    chartData() {
      return [
        {
          label: this.selectedResolution.diffChartSubtitle,
          backgroundColor: this.parsedDiffs.map((value) => {
            if (value === null) {
              return "#cbd5e1";
            }

            return value <= 0 ? "#059669" : "#dc2626";
          }),
          borderRadius: 6,
          barPercentage: 0.72,
          categoryPercentage: 0.7,
          data: this.parsedDiffs,
        },
      ];
    },
    chartLabels() {
      return this.averages.map((entry) => getChartXAxisDateFormat(entry.date));
    },
    chartOptions() {
      return {
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          mode: "index",
          intersect: false,
          backgroundColor: "#0f172a",
          titleFontColor: "#f8fafc",
          bodyFontColor: "#e2e8f0",
          displayColors: false,
          xPadding: 12,
          yPadding: 10,
          callbacks: {
            label: (tooltipItem) => `${tooltipItem.value > 0 ? "+" : ""}${tooltipItem.value} kg`,
          },
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
                drawBorder: false,
              },
              ticks: {
                fontColor: "#94a3b8",
                maxTicksLimit: 6,
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                color: "#e8eef5",
                drawBorder: false,
                zeroLineColor: "#cbd5e1",
              },
              ticks: {
                fontColor: "#94a3b8",
                maxTicksLimit: 5,
                min: -this.chartMinMax,
                max: this.chartMinMax,
                callback: (value) => `${value > 0 ? "+" : ""}${value}`,
              },
            },
          ],
        },
      };
    },
  },
};
</script>

<style scoped>
.weight-history-diff {
  height: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: none;
}

.weight-history-diff .card-body {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 380px;
  padding: 1.5rem;
}

.weight-history-diff__canvas {
  position: relative;
  flex: 0 0 auto;
  height: clamp(240px, 30vh, 300px);
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.weight-history-diff__canvas-element {
  height: 100%;
  width: 100%;
  min-width: 0;
}

.weight-history-diff__canvas-element /deep/ canvas {
  height: 100% !important;
  width: 100% !important;
  max-width: 100%;
  display: block;
}

.weight-history-diff__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.weight-history-diff__eyebrow {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.weight-history-diff__title {
  margin: 0.35rem 0 0;
  color: #0f172a;
  font-size: 1.1rem;
  font-weight: 700;
}

.weight-history-diff__pill {
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
  white-space: nowrap;
}

.weight-history-diff__pill--good {
  background: #dcfce7;
  color: #166534;
}

.weight-history-diff__pill--bad {
  background: #fee2e2;
  color: #991b1b;
}

.weight-history-diff__pill--neutral {
  background: #e2e8f0;
  color: #475569;
}

@media (max-width: 767px) {
  .weight-history-diff {
    border-radius: 14px;
  }

  .weight-history-diff .card-body {
    min-height: 340px;
    padding: 1rem;
  }

  .weight-history-diff__canvas {
    height: 220px;
  }

  .weight-history-diff__header {
    margin-bottom: 1rem;
  }

  .weight-history-diff__title {
    font-size: 1rem;
  }
}
</style>
